import { useState, useEffect, useCallback } from 'react'
import { PracticeTrackerState, LessonTool } from './types'
import { getLessonTool, getOrCreatePracticeTracker, getPracticeLogsCount, insertPracticeLog, updateGoal } from './api'

// lightweight retry/backoff (3 tries: ~0.4s, 0.8s, 1.2s)
async function withRetry<T>(fn: () => Promise<T>, tries = 3, delay = 400): Promise<T> {
  let lastErr: any
  for (let i = 0; i < tries; i++) {
    try {
      return await fn()
    } catch (e) {
      lastErr = e
      await new Promise(r => setTimeout(r, delay * (i + 1)))
    }
  }
  throw lastErr
}

export function usePracticeTracker(lessonToolId: string, currentUserId: string | null) {
  const [state, setState] = useState<PracticeTrackerState>({
    tracker: null,
    lessonTool: null,
    logCount: 0,
    loading: true,
    error: null,
    isUpdating: false
  })

  const [isButtonDisabled, setIsButtonDisabled] = useState(false)

  // Initialize tracker
  useEffect(() => {
    if (!currentUserId) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Not authenticated'
      }))
      return
    }
    initializeTracker()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonToolId, currentUserId])

  const initializeTracker = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }))

      const lessonTool = await withRetry(() => getLessonTool(lessonToolId))
      if (!lessonTool) throw new Error('Lesson tool not found or access denied')

      const tracker  = await withRetry(() => getOrCreatePracticeTracker(lessonTool, currentUserId!))
      const logCount = await withRetry(() => getPracticeLogsCount(tracker.id))

      setState(prev => ({
        ...prev,
        tracker,
        lessonTool: lessonTool as LessonTool,
        logCount,
        loading: false
      }))
    } catch (error: any) {
      const raw = (error && error.message) ? String(error.message) : ''
      const friendly = /timeout|terminated|connection.*timeout/i.test(raw)
        ? 'The database is busy right now. Please try again in a moment.'
        : (raw || 'An error occurred')
      setState(prev => ({
        ...prev,
        loading: false,
        error: friendly
      }))
    }
  }

  const addPracticeLog = useCallback(async () => {
    if (!state.tracker || isButtonDisabled) return

    // Prevent double-clicks
    setIsButtonDisabled(true)
    setTimeout(() => setIsButtonDisabled(false), 400)

    try {
      setState(prev => ({ ...prev, isUpdating: true }))

      // Optimistic update
      setState(prev => ({ ...prev, logCount: prev.logCount + 1 }))

      await insertPracticeLog(state.tracker.id, state.tracker.student_id)
    } catch (error) {
      // Revert optimistic update on error
      setState(prev => ({ ...prev, logCount: Math.max(0, prev.logCount - 1) }))
      console.error('Failed to add practice log:', error)
    } finally {
      setState(prev => ({ ...prev, isUpdating: false }))
    }
  }, [state.tracker, isButtonDisabled])

  const updateTrackerGoal = useCallback(async (newGoal: number) => {
    if (!state.tracker) return
    // clamp to a sane range
    const safeGoal = Math.max(1, Math.min(100, Math.floor(Number(newGoal) || 1)))

    try {
      setState(prev => ({ ...prev, isUpdating: true }))
      // Optimistic update
      setState(prev => ({
        ...prev,
        tracker: prev.tracker ? { ...prev.tracker, goal_sessions: safeGoal } : null
      }))

      await updateGoal(state.tracker.id, safeGoal)
    } catch (error) {
      // Revert on error - refetch to get correct state
      await initializeTracker()
      console.error('Failed to update goal:', error)
    } finally {
      setState(prev => ({ ...prev, isUpdating: false }))
    }
  }, [state.tracker])

  return {
    ...state,
    isButtonDisabled,
    addPracticeLog,
    updateTrackerGoal,
    refetch: initializeTracker
  }
}